import { useState } from 'react';
import { Size, instantFromWallClock, serviceRequestEditProblem, wallClockInZone, asSentence, t } from '@bms2/shared';
import { DatePicker, NumericInput, Select } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import modalStyles from '../../components/modals/modal.module.css';
import { useViewerZone } from '../../shell/ViewerZone';
import { patch, useApi } from './api';
import { requestEditPath, requestTiersPath, type ServiceRequestBandView } from './DealRequestBand';

/**
 * 🔒 The Service Request tile's Edit (decision 12600; T3815): the priority tier, the
 * preferred window and the ceiling. The tier is chosen from the tiers in force or left
 * to follow the window; the billing minimum and the job's tier follow the save.
 */
export function ServiceRequestEditModal({ dealId, band, apiBaseUrl = '', onCancel, onSaved }: { dealId: string; band: ServiceRequestBandView; apiBaseUrl?: string; onCancel: () => void; onSaved: () => void }) {
  const zone = useViewerZone();
  const tiers = useApi<{ tierKey: string; name: string }[]>(requestTiersPath(), apiBaseUrl);
  const options = tiers.state.kind === 'ready' && Array.isArray(tiers.state.data) ? tiers.state.data : [];
  // 🔒 The tile shows the tier in effect; saving it unchanged is not a choice, so only a differing pick is sent, and the
  // empty choice (Follow the preferred window) is sent as null, which clears a pinned tier (the SR1 review).
  const [tierKey, setTierKey] = useState<string>(band.tierKey ?? '');
  const [windowStart, setWindowStart] = useState(band.preferredWindowStart ? wallClockInZone(band.preferredWindowStart, zone) : '');
  const [windowEnd, setWindowEnd] = useState(band.preferredWindowEnd ? wallClockInZone(band.preferredWindowEnd, zone) : '');
  const [ceilingCents, setCeilingCents] = useState<number | null>(band.ceilingCents);
  const [error, setError] = useState<string | null>(null);
  // 🔒 Once a save is refused as stale, Save is held until the window is closed: its fields still read the old values, and another Save would overwrite the other person's change.
  const [stale, setStale] = useState(false);
  const save = async () => {
    setError(null);
    const at = (wall: string): string | null => (wall.trim() ? instantFromWallClock(wall.trim(), zone).toISOString() : null);
    let times: { preferredWindowStart: string | null; preferredWindowEnd: string | null };
    try {
      times = { preferredWindowStart: at(windowStart), preferredWindowEnd: at(windowEnd) };
    } catch {
      // silent: a time that is not a time is refused with a sentence, a refusal of what was typed.
      return setError(t('deal.request.timeInvalid'));
    }
    const problem = serviceRequestEditProblem({ ...times, ceilingCents });
    if (problem) return setError(asSentence(problem));
    const answer = await patch(requestEditPath(dealId), { ...(tierKey !== (band.tierKey ?? '') ? { tierKey: tierKey || null } : {}), ...times, ceilingCents, ...(band.version !== undefined ? { version: band.version } : {}) }, apiBaseUrl);
    if (!answer.ok) {
      // 🔒 A refusal for a request somebody else changed reads the record again, as a save does, so the next Edit opens on the fresh version (the EOD86C review).
      if (answer.status === 409) {
        setStale(true);
        onSaved();
      }
      return setError(answer.message);
    }
    onSaved();
    onCancel();
  };
  const typed = tierKey !== (band.tierKey ?? '') || windowStart !== (band.preferredWindowStart ? wallClockInZone(band.preferredWindowStart, zone) : '') || windowEnd !== (band.preferredWindowEnd ? wallClockInZone(band.preferredWindowEnd, zone) : '') || ceilingCents !== band.ceilingCents;
  return (
    <FormFrame
      host="window"
      open
      title={t('deal.request.edit.title')}
      onCancel={onCancel}
      dataForm="deal-request-edit"
      typed={typed}
      act={{ label: t('modal.save'), onAct: () => save(), ...(stale ? { disabled: true, disabledReason: t('deal.request.edit.stale') } : {}) }}
      refusal={error}
      onDismissRefusal={() => setError(null)}
    >
      <span data-request-edit-field="tier">
        <Select size={Size.Medium} label={t('deal.request.edit.tier')} value={tierKey} onChange={setTierKey} placeholder={t('deal.request.edit.tierDerived')} options={options.map((row) => ({ value: row.tierKey, label: row.name }))} />
        <p className={modalStyles.lead}>{t('deal.request.edit.tierHint')}</p>
      </span>
      <DatePicker size={Size.Medium} mode="datetime" label={t('deal.request.windowStart')} value={windowStart} onChange={setWindowStart} name="request-edit-window-start" />
      <DatePicker size={Size.Medium} mode="datetime" label={t('deal.request.windowEnd')} value={windowEnd} onChange={setWindowEnd} name="request-edit-window-end" {...(windowStart ? { min: windowStart } : {})} />
      <NumericInput size={Size.Medium} kind="currency" label={t('deal.request.ceiling')} value={ceilingCents} onChange={setCeilingCents} name="request-edit-ceiling" hint={t('deal.request.ceilingHint')} />
      <p className={modalStyles.lead}>{t('deal.request.edit.minimumFollows')}</p>
    </FormFrame>
  );
}
